import { fetchAuthSession } from '@aws-amplify/auth';
import { store } from '../../store/store';

/**
 * Gets the authentication token from Cognito
 * @returns The authentication token
 */
export const getAuthToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('Error fetching Cognito token:', error);
    return null;
  }
};

/**
 * Attempts to refresh the authentication token by forcing a refresh
 * @returns The new authentication token, or null if refresh failed
 */
export const refreshAuthToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const newToken = session.tokens?.idToken?.toString() || null;
    if (newToken) {
      localStorage.setItem('cognito_token', newToken);
    }
    return newToken;
  } catch (error) {
    console.error('Error refreshing Cognito token:', error);
    return null;
  }
};

/**
 * Developer utility: Simulates token revocation by corrupting the stored token
 * This is useful for testing the token refresh flow
 * Only available in development mode
 */
export const simulateTokenRevocation = (): void => {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('simulateTokenRevocation is only available in development mode');
    return;
  }

  const currentToken = localStorage.getItem('cognito_token');
  if (!currentToken) {
    console.warn('No token found to revoke');
    return;
  }

  // Corrupt the token by modifying it (this will cause authentication failures)
  // Handle tokens shorter than 10 characters by prepending instead of slicing
  const corruptedToken =
    currentToken.length >= 10
      ? currentToken.slice(0, -10) + 'REVOKED'
      : 'REVOKED' + currentToken;
  localStorage.setItem('cognito_token', corruptedToken);

  // Developer-facing logs to help test and validate the revocation flow
  // Only log in development to avoid noise in production
  console.log('🔴 Token revoked (simulated)');
  console.log('The next API call should trigger token refresh');
  console.log('Try making an API call or refreshing the page to test the refresh flow');
};

/**
 * Developer utility: Restores a valid token (if available from Amplify session)
 * This is useful for testing - restores the token after simulation
 * Only available in development mode
 */
export const restoreToken = async (): Promise<void> => {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('restoreToken is only available in development mode');
    return;
  }

  try {
    const refreshedToken = await refreshAuthToken();
    if (refreshedToken) {
      console.log('✅ Token restored successfully');
    } else {
      console.warn('⚠️ Failed to restore token - you may need to sign in again');
    }
  } catch (error) {
    console.error('Error restoring token:', error);
  }
};

/**
 * Gets the authentication token synchronously (for non-async contexts)
 * @returns The authentication token
 * 
 * NOTE: Return type inconsistency - on success returns { type: 'Bearer', value: string | null },
 * but on error returns null. Consider refactoring to use a discriminated union or always
 * return the same shape for consistency.
 */
export const getAuthTokenSync = (): { type: 'Bearer'; value: string | null } | null => {
  try {
    const token = localStorage.getItem('cognito_token');
    return { type: 'Bearer', value: token };
  } catch (error) {
    console.error('Error fetching Cognito token:', error);
    return null;
  }
};
